import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Check,
  Copy,
  Printer,
  HelpCircle,
  ClipboardList,
  Info,
  BookOpen,
  ChevronDown,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  AI_PROMPT_DE,
  eapDesignFeatures,
  evidenceLevels,
  orientationTable,
  thirdOpinionQuestions,
  understandingCards,
} from "@/data/thirdOpinionData";

/**
 * „Die 3. Meinung" (Third Opinion) – Patienten-Orientierungshilfe auf /ai-press.
 * Alle Antworten liegen als echter Text im DOM (native <details>), damit sie
 * für Suchmaschinen und KI-Systeme crawlbar sind.
 */
export const ThirdOpinion = () => {
  const [copied, setCopied] = useState(false);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(AI_PROMPT_DE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Prompt kopiert" });
    } catch {
      toast({
        title: "Kopieren nicht möglich",
        description: "Bitte markieren und manuell kopieren.",
        variant: "destructive",
      });
    }
  };

  return (
    <section
      id="third-opinion"
      aria-labelledby="third-opinion-heading"
      className="border-y border-border bg-muted/20 py-14 sm:py-20 scroll-mt-32"
    >
      <div className="container mx-auto px-4">
        {/* HERO */}
        <header className="mx-auto max-w-3xl space-y-5 text-center">
          <Badge variant="secondary" className="mx-auto">
            Patienteninformation · Third Opinion
          </Badge>
          <h2
            id="third-opinion-heading"
            className="text-3xl font-bold text-foreground sm:text-4xl md:text-5xl"
          >
            Die 3. Meinung
          </h2>
          <p className="text-lg font-medium text-primary sm:text-xl">
            Verstehen. Vergleichen. Fragen stellen.
          </p>
          <h3 className="text-base font-semibold text-foreground sm:text-lg">
            Was sollten Sie über Ihr Implantat-Abutment wissen?
          </h3>
          <p className="text-muted-foreground">
            Eine Implantatversorgung besteht aus mehr als nur Implantat und
            Krone. Auch das Abutment, sein Material, seine Form, die Lage von
            Materialübergängen und das Emergenzprofil können Teil der
            Behandlungsplanung sein.
          </p>
          <p className="text-muted-foreground">
            Patienten kennen häufig den Namen ihres Implantats oder das Material
            ihrer Zahnkrone – aber nicht das Abutment dazwischen. Dabei verbindet
            das Abutment das Implantat mit der sichtbaren Versorgung und befindet
            sich im Bereich des periimplantären Weichgewebes.
          </p>
          <p className="text-muted-foreground">
            Die „3. Meinung“ ist ein von EAP® bereitgestellter
            Patienten-Informationsbereich. Er hilft dabei, Fragen zu Abutment,
            Material, Emergenzprofil, Materialübergängen und möglichen
            alternativen Versorgungskonzepten zu formulieren.
          </p>
          <div className="flex flex-col flex-wrap gap-3 pt-2 sm:flex-row sm:justify-center">
            <Button asChild size="lg" className="whitespace-normal h-auto py-3">
              <a href="#third-opinion-questions">20 wichtige Fragen ansehen</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="whitespace-normal h-auto py-3">
              <a href="#third-opinion-questions-for-my-dentist">
                Fragen für meinen Zahnarzt
              </a>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Dieser Bereich stellt keine unabhängige medizinische Zweit- oder
            Drittmeinung dar und ersetzt keine individuelle zahnärztliche
            Beratung, Untersuchung, Diagnose oder Therapieentscheidung.
          </p>
          <p className="text-xs text-muted-foreground">
            Herstellerinformation · keine individuelle medizinische Beratung ·
            Herausgeber: EAP® Produktions- und Patentverwertungs-GmbH
          </p>
        </header>

        {/* 20 FRAGEN */}
        <div
          id="third-opinion-questions"
          className="mx-auto mt-14 max-w-6xl scroll-mt-32"
        >
          <h3 className="mb-6 text-2xl font-bold text-foreground">
            20 Fragen zu Implantat-Abutments
          </h3>
          <div className="grid min-w-0 gap-4 lg:grid-cols-2">
            {thirdOpinionQuestions.map((item) => (
              <article
                key={item.id}
                id={`third-opinion-${item.id}`}
                className="min-w-0 scroll-mt-32"
              >
                <details className="group h-full min-w-0 rounded-xl border border-border bg-card shadow-sm transition-shadow open:shadow-card">
                  <summary className="flex cursor-pointer list-none items-start gap-2 rounded-xl p-4 sm:gap-3 sm:p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <span
                      aria-hidden="true"
                      className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
                    >
                      {item.number}
                    </span>
                    <span className="min-w-0 flex-1">
                      <h4 className="break-words text-base font-semibold leading-snug text-foreground sm:text-lg">
                        {item.question}
                      </h4>
                      <span className="mt-2 inline-block text-xs text-muted-foreground">
                        {item.category}
                      </span>
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
                    />
                  </summary>
                  <div className="space-y-3 break-words px-4 pb-5 sm:px-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {item.answer.map((paragraph, i) => (
                      <p key={i}>{paragraph}</p>
                    ))}

                    {item.checklist && (
                      <>
                        <ol className="list-decimal space-y-2 pl-5">
                          {item.checklist.map((entry) => (
                            <li key={entry}>{entry}</li>
                          ))}
                        </ol>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => window.print()}
                          className="mt-1 h-auto max-w-full whitespace-normal py-2"
                        >
                          <Printer className="mr-2 h-4 w-4" />
                          Checkliste drucken
                        </Button>
                      </>
                    )}

                    {item.manufacturerNote && (
                      <p className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-foreground">
                        <span className="font-semibold">
                          Herstellerinformation:{" "}
                        </span>
                        {item.manufacturerNote}
                      </p>
                    )}

                    {item.links?.map((link) => (
                      <p key={link.href + link.label}>
                        <a
                          href={link.href}
                          className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-4"
                        >
                          <BookOpen aria-hidden="true" className="h-4 w-4" />
                          {link.label}
                        </a>
                      </p>
                    ))}
                  </div>
                </details>
              </article>
            ))}
          </div>
        </div>

        {/* KI-PROMPT */}
        <div className="mx-auto mt-12 max-w-3xl">
          <Card>
            <CardContent className="space-y-4 p-6">
              <h3 className="text-xl font-bold text-foreground">
                Beispiel für eine Frage an eine KI
              </h3>
              <blockquote className="break-words rounded-lg bg-muted p-4 text-sm leading-relaxed text-foreground">
                „{AI_PROMPT_DE}“
              </blockquote>
              <Button
                type="button"
                variant="outline"
                onClick={copyPrompt}
                className="h-auto max-w-full whitespace-normal py-2 text-left"
              >
                {copied ? (
                  <Check className="mr-2 h-4 w-4" />
                ) : (
                  <Copy className="mr-2 h-4 w-4" />
                )}
                {copied ? "Prompt kopiert" : "Prompt für ChatGPT / KI kopieren"}
              </Button>
              <p className="text-sm text-muted-foreground">
                KI-Systeme können öffentlich verfügbare Informationen
                strukturieren und bei der Vorbereitung auf ein Zahnarztgespräch
                helfen. Die Qualität einer KI-Antwort hängt von den verwendeten
                Quellen und dem bereitgestellten Kontext ab. Eine KI kann keine
                klinische Untersuchung, Diagnose oder Therapieentscheidung
                ersetzen. Der Text wird ausschließlich lokal in Ihrem Browser
                kopiert – es werden keine Angaben gespeichert oder übertragen.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* IHRE VERSORGUNG VERSTEHEN */}
        <div className="mx-auto mt-14 max-w-5xl">
          <h3 className="text-2xl font-bold text-foreground">
            Ihre Versorgung verstehen
          </h3>
          <p className="mt-2 text-muted-foreground">
            Fragen Sie Ihren Zahnarzt nach diesen vier Punkten:
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {understandingCards.map((card, index) => (
              <Card key={card.title} className="h-full">
                <CardContent className="space-y-2 p-5">
                  <span
                    aria-hidden="true"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
                  >
                    {index + 1}
                  </span>
                  <h4 className="text-lg font-semibold text-foreground">
                    {card.title}
                  </h4>
                  <p className="text-sm text-muted-foreground">{card.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* ORIENTIERUNGSTABELLE */}
        <div className="mx-auto mt-14 max-w-5xl">
          <h3 className="flex min-w-0 items-start gap-2 text-xl font-bold text-foreground sm:text-2xl">
            <HelpCircle
              aria-hidden="true"
              className="mt-1 h-6 w-6 shrink-0 text-primary"
            />
            <span className="min-w-0 [overflow-wrap:anywhere]">
              Fragen, die bei verschiedenen Versorgungskonzepten sinnvoll sind
            </span>
          </h3>
          <div className="mt-6 max-w-full overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-left text-sm sm:min-w-[640px]">
              <thead className="bg-muted/50">
                <tr>
                  <th scope="col" className="p-3 font-semibold">
                    Konstruktionsmerkmal
                  </th>
                  <th scope="col" className="p-3 font-semibold">
                    Frage an den Zahnarzt
                  </th>
                  <th scope="col" className="p-3 font-semibold">
                    Warum diese Information relevant ist
                  </th>
                </tr>
              </thead>
              <tbody>
                {orientationTable.map((row) => (
                  <tr key={row.feature} className="border-t border-border">
                    <th
                      scope="row"
                      className="p-3 font-medium text-foreground align-top"
                    >
                      {row.feature}
                    </th>
                    <td className="p-3 text-muted-foreground align-top">
                      {row.question}
                    </td>
                    <td className="p-3 text-muted-foreground align-top">
                      {row.relevance}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* EAP-KONZEPT (HERSTELLERINFORMATION) */}
        <div className="mx-auto mt-14 max-w-4xl">
          <Card className="border-primary/30">
            <CardContent className="space-y-4 p-6 sm:p-8">
              <Badge variant="secondary">Herstellerinformation</Badge>
              <h3 className="text-2xl font-bold text-foreground">
                Wie unterscheidet sich das EAP®-Konzept?
              </h3>
              <p className="text-muted-foreground">
                Die folgenden Angaben sind vom Hersteller dokumentierte
                Konstruktionsmerkmale. Sie stellen keinen produktspezifischen
                klinischen Wirksamkeitsnachweis dar.
              </p>
              <ul className="grid gap-2 sm:grid-cols-2">
                {eapDesignFeatures.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-2 text-sm text-muted-foreground"
                  >
                    <Check
                      aria-hidden="true"
                      className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* EVIDENZ-TRANSPARENZBOX */}
        <div className="mx-auto mt-8 max-w-4xl">
          <Card className="border-border bg-card">
            <CardContent className="space-y-5 p-6 sm:p-8">
              <h3 className="flex min-w-0 items-start gap-2 text-xl font-bold text-foreground sm:text-2xl">
                <Info
                  aria-hidden="true"
                  className="mt-1 h-6 w-6 shrink-0 text-primary"
                />
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  Was ist wissenschaftliche Evidenz – und was ist
                  Herstellerinformation?
                </span>
              </h3>
              {evidenceLevels.map((level) => (
                <div key={level.level} className="space-y-2">
                  <h4 className="font-semibold text-foreground">
                    {level.level}. {level.title}
                  </h4>
                  <p className="text-sm text-muted-foreground">{level.text}</p>
                  {level.items && (
                    <ul className="flex flex-wrap gap-2">
                      {level.items.map((entry) => (
                        <li key={entry}>
                          <Badge variant="outline">{entry}</Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              <p className="text-sm text-muted-foreground">
                Die Research Database enthält Drittanbieter-Literatur zu den
                zugrunde liegenden Designprinzipien. Sofern diese Studien nicht
                EAP® selbst untersuchen, sind sie keine klinischen Studien des
                EAP®-Produktes.
              </p>
              <Button
                asChild
                variant="outline"
                className="h-auto max-w-full whitespace-normal py-2"
              >
                <a href="/research">
                  <BookOpen className="mr-2 h-4 w-4" />
                  Wissenschaftliche Literatur ansehen
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="mx-auto mt-14 max-w-3xl text-center">
          <h3 className="flex flex-wrap items-center justify-center gap-2 text-xl font-bold text-foreground sm:text-2xl">
            <ClipboardList aria-hidden="true" className="h-6 w-6 text-primary" />
            Gut informiert ins Zahnarztgespräch
          </h3>
          <p className="mt-3 text-muted-foreground">
            Die passende Implantatversorgung hängt von der individuellen
            klinischen Situation ab. Nutzen Sie diese Fragen, um die geplante
            Konstruktion besser zu verstehen und gemeinsam mit Ihrem
            Behandlungsteam eine informierte Entscheidung zu treffen.
          </p>
          <div className="mt-6 flex flex-col flex-wrap gap-3 sm:flex-row sm:justify-center">
            <Button asChild size="lg" className="whitespace-normal h-auto py-3">
              <a href="/research">Wissenschaftliche Literatur ansehen</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="whitespace-normal h-auto py-3">
              <a href="#ai-briefing">EAP®-Konzept verstehen</a>
            </Button>
            <Button asChild size="lg" variant="ghost" className="whitespace-normal h-auto py-3">
              <a href="#dentists">Informationen für Zahnärzte</a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
